import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../../components/alertProvider';
import NoStockFound from '../../Inventory/ReusableComponents/NoStockFound';
import { useClinic } from '../../../components/ClinicProvider';
import { Box, Chip, Button, Grid2, IconButton, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Collapse, Popover, Tooltip, } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { ReactComponent as SettelIcon } from '../../../assets/SVG/Frame 1171275375.svg';
import { ReactComponent as DolorIcon } from '../../../assets//SVG/dolorIcon.svg';

import { ReactComponent as SearchIcon } from '../../../assets/SVG/Search-Icon.svg';
import { ReactComponent as Filter } from '../../../assets/SVG/filter.svg';
import { ReactComponent as Add } from '../../../assets/SVG/AddBlack.svg';
import { ReactComponent as PrintIcon } from '../../../assets/SVG/printerblue.svg';
import { ReactComponent as Note } from '../../../assets/SVG/receipt-edit.svg';

import { HeaderSx, tableScrollSx, selectedTableCellSx, openTableCellSx, primaryButtonSx, secondaryButtonSx, } from '../../Inventory/Common/inventory.helper';
import { FilterIconSx, AddIconSx, filterSearchTextBoxSx } from '../../Inventory/Stocks/stock.helper';
import StatusTabButtonGroup from '../../Inventory/ReusableComponents/StatusTabButtonGroup';
import CustomPagination from '../../cryoStorage/customPagination';
import { ReactComponent as InfoIcon } from '../../../assets/SVG/info-circle-red.svg';

import { SalesReturn, SalesReturnTab, getSalesReturnTabs, getSalesReturnItemsTableHeaders, SALES_RETURN_ITEMS_TABLE_MIN_WIDTH_PX, getSalesReturnTableHeaders, SALES_RETURN_TABLE_BODY_TEXT_SX, SALES_RETURN_TABLE_HEADER_TEXT_SX, SALES_RETURN_TABLE_SUBTEXT_SX, getSalesReturnHeaderCellSx, getSalesReturnItemBodyCellSx, getSalesReturnItemHeaderCellSx, useApproveSalesReturn, useRejectSalesReturn, useCreateSalesReturnPayment, useBillingConfigOptions, SalesReturnFilterValues, getStatusColor, useSalesReturnsQuery, PaymentMode, } from './SalesReturnHelper';
import SalesReturnFilterModal from './SalesReturnFilterModal';
import RejectSalesReturnModal, { RejectSalesReturnValues } from './RejectSalesReturnModal';
import IndentApproveCancelDialog from '../../StockTransfer/Indent/IndentApproveCancelDialog';
import SalesReturnSettleDialog from './SalesReturnSettleDialog/SalesReturnSettleDialog';
import SalesReturnReport from './SalesReturnReport';
import { CollectPaymentModal, type PaymentDetails, type PaymentFormValues, type PaymentHistoryItem, } from '../../Billing/collectPayment';
import useCurrency from '../../../components/CurrencyProvider';
import TableSkeleton from '../../../components/common/TableSkeleton';
import { getSalesReturnSkeletonColumnWidths } from '../helpers';
import HeaderProfile from '../../../components/header_Profile/header_Profile.index';
import { getButtonStates } from '../../../utils/permissionService';

const PAYMENT_METHOD_LABEL_TO_MODE_NAME: Record<string, string> = {
  'Cash Payment': 'Cash',
  'Cheque': 'Cheque',
  'Bank': 'Online',
  'Card Payment': 'Card',
  'UPI': 'UPI/Wallet',
  'Wallet': 'Advance',
};

export interface SalesReturnListBaseProps {
  /** When provided, filters by patient and hides Patient column */
  patientId?: number | null;
  /** Whether to show HeaderProfile component */
  showHeaderProfile?: boolean;
}

export const SalesReturnListBase: React.FC<SalesReturnListBaseProps> = ({
  patientId,
  showHeaderProfile = false,
}) => {
  const { t } = useTranslation('sales_return');
  const navigate = useNavigate();
  const { addAlert } = useAlert();
  const { ClinicId } = useClinic();
  const { canEdit, canAdd, canPrint } = getButtonStates('Admin', 'Return', 'Pharmacy Return');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<string>('All');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(patientId ? 10 : 8);
  const currency = useCurrency();
  const { currencySymbol } = currency;
  const [filterValues, setFilterValues] = useState<SalesReturnFilterValues>({
    fromDate: '',
    toDate: '',
    settlementStatus: 'unsettled',
  });

  // Use server-side pagination when patientId is provided, client-side otherwise
  const useServerPagination = !!patientId;

  const { data: apiResponse, isLoading, error: queryError } = useSalesReturnsQuery({
    limit: useServerPagination ? rowsPerPage : 100,
    offset: useServerPagination ? page * rowsPerPage : 0,
    status: selectedTab !== 'All' ? selectedTab : undefined,
    search: searchTerm,
    fromDate: filterValues.fromDate,
    toDate: filterValues.toDate,
    settlementStatus: filterValues.settlementStatus,
    clinicId: ClinicId ? parseInt(ClinicId, 10) : undefined,
    patientId: patientId ?? undefined,
    debounceMs: 350,
    enabled: true,
  });

  // Initialize mutation hooks
  const approveMutation = useApproveSalesReturn();
  const rejectMutation = useRejectSalesReturn();
  const createPaymentMutation = useCreateSalesReturnPayment();

  const salesReturns = useMemo(() => apiResponse?.objects ?? [], [apiResponse]);
  const statusCounts = apiResponse?.status_counts;
  const error = queryError ? 'Failed to load Sales returns data' : null;

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [settlePaymentDialogOpen, setSettlePaymentDialogOpen] = useState(false);
  const [addToAdvanceConfirmDialogOpen, setAddToAdvanceConfirmDialogOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRowForAction, setSelectedRowForAction] = useState<SalesReturn | null>(null);

  const [isPerformingAction, setIsPerformingAction] = useState(false);

  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundModalPending, setRefundModalPending] = useState(false);
  const [refundPaymentDetails, setRefundPaymentDetails] = useState<PaymentDetails | null>(null);
  const [, setRefundPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [billingConfigPatientId, setBillingConfigPatientId] = useState<number | null>(null);

  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [selectedRowForPrint, setSelectedRowForPrint] = useState<SalesReturn | null>(null);

  // Fetch billing config options when patient ID is set
  const { data: billingConfigOptions } = useBillingConfigOptions(billingConfigPatientId);

  const paymentModesForModal = useMemo(
    () =>
      (billingConfigOptions?.modeofpayment ?? []).map((m) => ({
        id: m.id,
        name: m.name,
        code: String(m.code),
      })),
    [billingConfigOptions?.modeofpayment]
  );

  // Open refund modal only after payment modes are available
  useEffect(() => {
    if (!refundModalPending) return;
    if (paymentModesForModal.length > 0) {
      setRefundModalOpen(true);
      setRefundModalPending(false);
    }
  }, [refundModalPending, paymentModesForModal.length]);

  // Generate tab data with counts from API response
  const tabsData = useMemo<SalesReturnTab[]>(() => {
    if (!statusCounts) return getSalesReturnTabs(t);

    return [
      { label: t('Status_All'), value: 'All', count: statusCounts.total_count },
      { label: t('Status_Pending'), value: 'pending', count: statusCounts.pending_count },
      { label: t('Status_Approved'), value: 'approved', count: statusCounts.approved_count },
      { label: t('Status_Rejected'), value: 'rejected', count: statusCounts.rejected_count },
    ];
  }, [statusCounts, t]);

  const openApproveDialog = (row: SalesReturn) => {
    setSelectedRowForAction(row);
    setApproveDialogOpen(true);
  };

  const openSettlePaymentDialog = (row: SalesReturn) => {
    setSelectedRowForAction(row);
    setSettlePaymentDialogOpen(true);
  };

  const openRejectModal = (row: SalesReturn) => {
    setSelectedRowForAction(row);
    setRejectModalOpen(true);
  };

  const closeApproveDialog = () => {
    if (!isPerformingAction) {
      setApproveDialogOpen(false);
      setSelectedRowForAction(null);
    }
  };

  const closeSettlePaymentDialog = useCallback(() => {
    if (!isPerformingAction) {
      setSettlePaymentDialogOpen(false);
    }
  }, [isPerformingAction]);

  const closeAddToAdvanceConfirmDialog = useCallback(() => {
    if (!isPerformingAction) {
      setAddToAdvanceConfirmDialogOpen(false);
      setSelectedRowForAction(null);
    }
  }, [isPerformingAction]);

  const closeRejectModal = () => {
    if (!isPerformingAction) {
      setRejectModalOpen(false);
      setSelectedRowForAction(null);
    }
  };

  const [remarkAnchorEl, setRemarkAnchorEl] = useState<HTMLElement | null>(null);
  const [remarkText, setRemarkText] = useState<string>('');
  const [remarkTitle, setRemarkTitle] = useState(t('Reject_Remark'));

  const closeRemarkPopover = useCallback(() => {
    setRemarkAnchorEl(null);
    setRemarkText('');
    setRemarkTitle(t('Reject_Remark'));
  }, [t]);

  // Show a red dot on filter icon when filters are active
  useEffect(() => {
    const hasFilters =
      !!(filterValues.fromDate || filterValues.toDate) ||
      filterValues.settlementStatus !== 'unsettled';
    setHasActiveFilters(hasFilters);
  }, [filterValues]);

  const confirmApprove = async () => {
    if (!selectedRowForAction) return;

    setIsPerformingAction(true);

    try {
      const response = await approveMutation.mutateAsync(selectedRowForAction.id);
      if (response?.message) {
        addAlert(response.message, 'success');
      }
      setApproveDialogOpen(false);
      setSelectedRowForAction(null);
      setExpandedRow(null);
    } catch {
      addAlert('Failed to approve sales return', 'error');
    } finally {
      setIsPerformingAction(false);
    }
  };

  const handleRejectSubmit = async (values: RejectSalesReturnValues) => {
    if (!selectedRowForAction) return;

    setIsPerformingAction(true);

    try {
      const response = await rejectMutation.mutateAsync({
        id: selectedRowForAction.id,
        rejectRemark: values.rejectRemark,
      });
      if (response?.message) {
        addAlert(response.message, 'success');
      }
      setRejectModalOpen(false);
      setSelectedRowForAction(null);
      setExpandedRow(null);
    } catch {
      addAlert('Failed to reject sales return', 'error');
    } finally {
      setIsPerformingAction(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    setPage(0);
  };

  // Pagination data - server-side or client-side
  const paginatedData = useMemo(() => {
    if (useServerPagination) {
      return salesReturns;
    }
    const start = page * rowsPerPage;
    return salesReturns.slice(start, start + rowsPerPage);
  }, [salesReturns, page, rowsPerPage, useServerPagination]);

  const totalCount = useServerPagination
    ? (apiResponse?.meta?.total_count ?? 0)
    : salesReturns.length;

  const actionRow = useMemo(() => {
    return (
      selectedRowForAction ?? salesReturns.find((r) => r.id === expandedRow) ?? null
    );
  }, [selectedRowForAction, salesReturns, expandedRow]);

  const handleExpandRow = (id: number) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const handleFilterClick = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleFilterSave = (values: SalesReturnFilterValues) => {
    setFilterValues(values);
    setPage(0);
  };

  const handlePageSizeChange = useCallback((size: number) => {
    setRowsPerPage(size);
    setPage(0);
  }, []);

  const handleAddNew = () => {
    if (patientId) {
      navigate(`/admin/inventory/return/sales/newsalesreturn/${patientId}`);
    } else {
      navigate('/admin/inventory/return/sales/new');
    }
  };

  const openRefundModal = useCallback((row: SalesReturn) => {
    const amountToRefund = Number(row.balance_amount ?? 0);
    if (!amountToRefund || amountToRefund <= 0) return;

    const details: PaymentDetails = {
      date: new Date().toLocaleDateString('en-GB'),
      patientName: row.patient_name || 'Patient',
      patientId: row.patient_mr_no || '',
      currency: 'INR',
      netAmount: amountToRefund,
    };

    setRefundPaymentDetails(details);
    setBillingConfigPatientId(row.patient_id);
    setRefundModalPending(true);
  }, []);

  const handleRefundSave = useCallback(
    async (_data: PaymentFormValues, history: PaymentHistoryItem[]) => {
      const row = selectedRowForAction;
      if (!row || history.length === 0) {
        setRefundModalOpen(false);
        return;
      }

      setIsPerformingAction(true);
      try {
        const paymentModes = billingConfigOptions?.modeofpayment ?? [];

        const resolvePaymentModeId = (payment: PaymentHistoryItem): number => {
          if (payment.payment_mode_id) return payment.payment_mode_id;

          const modeName = PAYMENT_METHOD_LABEL_TO_MODE_NAME[payment.method] ?? '';
          const matched = paymentModes.find((m: PaymentMode) => m.name === modeName);

          if (!matched) {
            throw new Error('Unable to resolve payment mode for refund payment.');
          }
          return matched.id;
        };

        const paymentDetails = history.map((payment) => ({
          payment_mode_id: resolvePaymentModeId(payment),
          number: payment.transactionNumber || payment.chequeNumber || undefined,
          paid_amount: payment.amount.toString(),
          bank_id: undefined,
          account_name: undefined,
          branch_name: undefined,
          ifsc_code: undefined,
          package_advance_id: undefined,
          credit_transaction_consumptions: payment.credit_transaction_consumptions,
        }));

        const response = await createPaymentMutation.mutateAsync({
          id: row.id,
          payload: {
            settlement_option: 'REFUND_TO_PATIENT',
            payment_details: paymentDetails,
            amount: row.balance_amount || '0',
            advance_type: '',
          },
        });

        if (response?.message) {
          addAlert(response.message, 'success');
        }
        setRefundPaymentHistory(history);
        setRefundModalOpen(false);
        setSelectedRowForAction(null);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to process refund payment';
        addAlert(message, 'error');
      } finally {
        setIsPerformingAction(false);
      }
    },
    [selectedRowForAction, createPaymentMutation, addAlert, billingConfigOptions]
  );

  const openAddToAdvanceConfirmDialog = useCallback(() => {
    closeSettlePaymentDialog();
    setAddToAdvanceConfirmDialogOpen(true);
  }, [closeSettlePaymentDialog]);

  const handleSettleAddToAdvance = useCallback(async () => {
    const row = selectedRowForAction;
    if (!row) return;

    setIsPerformingAction(true);

    try {
      const response = await createPaymentMutation.mutateAsync({
        id: row.id,
        payload: {
          settlement_option: 'ADD_TO_ADVANCE',
          amount: row.balance_amount || '0',
          advance_type: 'CHECKIN',
        },
      });

      if (response?.message) {
        addAlert(response.message, 'success');
      }
      closeAddToAdvanceConfirmDialog();
      setSelectedRowForAction(null);
    } catch {
      addAlert('Failed to add to advance', 'error');
    } finally {
      setIsPerformingAction(false);
    }
  }, [selectedRowForAction, createPaymentMutation, addAlert, closeAddToAdvanceConfirmDialog]);

  const handleSettleRefundToPatient = useCallback(() => {
    const row = selectedRowForAction;
    closeSettlePaymentDialog();
    if (row) {
      openRefundModal(row);
    }
  }, [selectedRowForAction, closeSettlePaymentDialog, openRefundModal]);

  const handlePrintClick = useCallback((row: SalesReturn) => {
    setSelectedRowForPrint(row);
    setPrintPreviewOpen(true);
  }, []);

  const inlineLabelSx = {
    ...SALES_RETURN_TABLE_HEADER_TEXT_SX,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  } as const;

  const getStatusTranslationKey = (status: string): string => {
    const statusMap: Record<string, string> = {
      'pending': 'Status_Pending',
      'approved': 'Status_Approved',
      'rejected': 'Status_Rejected',
      'cancelled': 'Status_Cancelled',
      'draft': 'Status_Draft',
    };
    return statusMap[status.toLowerCase()] || status;
  };

  const renderStatusChip = useCallback((status: string) => {
    const { bgColor, textColor } = getStatusColor(status);
    const translationKey = getStatusTranslationKey(status);
    const translatedLabel = translationKey.startsWith('Status_') ? t(translationKey) : status;
    return (
      <Chip
        label={translatedLabel}
        size="small"
        sx={{
          backgroundColor: bgColor,
          color: textColor,
          fontWeight: 500,
          fontSize: '12px',
          fontFamily: 'Montserrat',
        }}
      />
    );
  }, [t]);

  // Filter headers based on whether patient is selected
  const filteredHeaders = useMemo(() => {
    const headers = getSalesReturnTableHeaders(t);
    if (patientId) {
      return headers.filter((label) => label !== t('Table_Header_Patient'));
    }
    return headers;
  }, [patientId, t]);

  return (
    <>
      {showHeaderProfile && <HeaderProfile onData={() => {}} />}
      <Grid2
        data-testid="sales-return-container"
        height={'100%'}
        width={'100%'}
        container
        display={'flex'}
        flexDirection={'column'}
      >
        {/* Header Section */}
        <Grid2
          data-testid="sales-return-header-section"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          px={2}
          mb={0}
          size={12}
        >
          <Grid2 container size={12} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <Typography sx={HeaderSx}>{t('List_Title')}</Typography>

            <Grid2 display={'flex'} justifyContent={'flex-end'} alignItems={'center'} gap={1}>
              <Grid2 width={400}>
                <TextField
                  size="small"
                  placeholder={t('Search_Placeholder')}
                  variant="outlined"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    ...filterSearchTextBoxSx,
                    '& .MuiOutlinedInput-root': {
                      ...filterSearchTextBoxSx['& .MuiOutlinedInput-root'],
                      height: '43px',
                    },
                  }}
                />
              </Grid2>

              <Grid2>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <IconButton onClick={handleFilterClick}>
                    <Filter style={FilterIconSx} />
                  </IconButton>
                  {hasActiveFilters && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#f44336',
                        border: '1px solid white',
                        boxShadow: '0 0 0 1px rgba(244, 67, 54, 0.3)',
                      }}
                    />
                  )}
                </Box>
              </Grid2>

              {(canEdit || canAdd) && (
                <Grid2>
                  <IconButton data-testid="sales-return-add-btn" onClick={handleAddNew}>
                    <Add style={AddIconSx} />
                  </IconButton>
                </Grid2>
              )}
            </Grid2>
          </Grid2>
        </Grid2>

        {/* Status Tabs */}
        <Box  sx={{marginLeft:'22px'}}>
          <StatusTabButtonGroup
            data-testid="sales-return-tabs"
            options={tabsData}
            selected={selectedTab}
            onChange={handleTabChange}
          />
        </Box>
        {/* Table Section */}
        <Grid2 height={'calc(100% - 185px)'} mt={2} size={12} px={2}>
          <TableContainer sx={{ borderRadius: '16px', overflow: 'hidden', ...tableScrollSx }}>
            <Table>
              <TableHead>
                <TableRow>
                  {filteredHeaders.map((label, index) => {
                    const isAmountColumn = 
                      label === t('Table_Header_Return_Amount') ||
                      label === t('Table_Header_Paid_Amount') ||
                      label === t('Table_Header_Balance_Amount');
                    const labelWithCurrency = isAmountColumn
                      ? `${label} (${currencySymbol})`
                      : label;
                    return (
                      <TableCell
                        key={label}
                        sx={getSalesReturnHeaderCellSx(index, filteredHeaders.length)}
                      >
                        <Typography sx={SALES_RETURN_TABLE_HEADER_TEXT_SX}>
                          {labelWithCurrency}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableSkeleton
                    columnWidths={getSalesReturnSkeletonColumnWidths(selectedTab, !patientId)}
                    rows={rowsPerPage}
                  />
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={filteredHeaders.length} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: '#f44336', fontFamily: 'Montserrat' }}>
                        {error}
                      </Typography>
                      <Typography
                        sx={{ color: '#9E9E9E', fontFamily: 'Montserrat', fontSize: '12px', mt: 1 }}
                      >
                        No data available. Please try again later.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={filteredHeaders.length} align="center" sx={{ py: 4 }}>
                      <NoStockFound message={t('No_Sales_Returns_Found')} />
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row) => {
                    const isExpanded = expandedRow === row.id;
                    return (
                      <React.Fragment key={row.id}>
                        <TableRow
                          onClick={() => handleExpandRow(row.id)}
                          sx={{
                            cursor: 'pointer',
                            ...openTableCellSx(isExpanded),
                          }}
                        >
                          <TableCell sx={{ ...selectedTableCellSx(isExpanded), ...openTableCellSx(isExpanded) }} >
                            <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                              {row.return_number}
                            </Typography>
                            <Typography sx={SALES_RETURN_TABLE_SUBTEXT_SX}>
                              {row.return_date}
                            </Typography>
                          </TableCell>
                          {/* Patient column - only shown when no patientId filter */}
                          {!patientId && (
                            <TableCell
                              sx={{ ...selectedTableCellSx(isExpanded), ...openTableCellSx(isExpanded) }}
                            >
                              <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                {row.patient_name} | {row.patient_age}
                              </Typography>
                              <Typography sx={SALES_RETURN_TABLE_SUBTEXT_SX}>
                                {row.patient_mr_no} | {row.patient_gender}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell
                            sx={{ ...selectedTableCellSx(isExpanded), ...openTableCellSx(isExpanded) }}
                          >
                            <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                              {Number(row.net_amount).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{ ...selectedTableCellSx(isExpanded), ...openTableCellSx(isExpanded) }}
                          >
                            <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                              {Number(row.paid_amount).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{ ...selectedTableCellSx(isExpanded), ...openTableCellSx(isExpanded) }}
                          >
                            <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                              {Number(row.balance_amount).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{ ...selectedTableCellSx(isExpanded), ...openTableCellSx(isExpanded) }}
                          >
                            {renderStatusChip(row.status)}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...selectedTableCellSx(isExpanded),
                              ...openTableCellSx(isExpanded),
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={row.remarks || ''}
                          >
                            <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                              {row.remarks || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{ ...selectedTableCellSx(isExpanded), ...openTableCellSx(isExpanded) }}
                          >
                            <Box
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 1,
                              }}
                            >
                              {row.status === 'approved' &&
                                row.balance_amount &&
                                Number(row.balance_amount) > 0 && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openSettlePaymentDialog(row);
                                  }}
                                  sx={{ width: 32, height: 32 }}
                                >
                                  <Note fontSize="small" />
                                </IconButton>
                              )}
                              {row.status === 'rejected' && (
                                <Tooltip
                                  title={
                                    <Typography sx={{ fontSize: '12px', fontFamily: 'Montserrat' }}>
                                      <strong>Reject Remark:</strong> {row.rejection_reason || '-'}
                                    </Typography>
                                  }
                                  arrow
                                  placement="bottom"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: '#ffffff',
                                        maxWidth: 260,
                                        padding: '8px 12px',
                                        color: '#000000',
                                        boxShadow:
                                          '0px 2px 4px rgba(0, 0, 0, 0.1), 0px 8px 16px rgba(0, 0, 0, 0.1)',
                                        borderRadius: '8px',
                                        fontFamily: 'Montserrat',
                                      },
                                    },
                                  }}
                                >
                                  <Box
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <InfoIcon />
                                  </Box>
                                </Tooltip>
                              )}

                              {canPrint && (
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrintClick(row);
                                  }}
                                  aria-label={t('Print_Report')}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    p: 0,
                                  }}
                                >
                                  <PrintIcon />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Expanded Row for Item Details */}
                        <TableRow
                          sx={{
                            borderBottom: isExpanded ? 'none' : '1px solid rgba(224, 224, 224, 1)',
                          }}
                        >
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={filteredHeaders.length}
                            sx={{
                              ...selectedTableCellSx(isExpanded),
                              borderBottom: isExpanded ? 'none' : '1px solid rgba(224, 224, 224, 1)',
                            }}
                          >
                            <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 0, bgcolor: '#FFF8F6' }}>
                                {row.return_items && row.return_items.length > 0 ? (
                                  <Box sx={{ padding: 2 }}>
                                    <Box
                                      sx={{
                                        ...tableScrollSx,
                                        width: '100%',
                                        height: '100%',
                                        overflowX: 'auto',
                                      }}
                                    >
                                      <TableContainer
                                        sx={{ minWidth: `${SALES_RETURN_ITEMS_TABLE_MIN_WIDTH_PX}px`, maxWidth: '100%', overflowX: 'auto' }}
                                      >
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              {getSalesReturnItemsTableHeaders(t).map((header, index, arr) => (
                                                <TableCell
                                                  key={header}
                                                  sx={getSalesReturnItemHeaderCellSx(index, arr.length)}
                                                >
                                                  <Typography sx={SALES_RETURN_TABLE_HEADER_TEXT_SX}>
                                                    {header}
                                                  </Typography>
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {row.return_items.map((item, itemIndex) => (
                                              <TableRow
                                                key={item.id || itemIndex}
                                                sx={{
                                                  '&:last-child td': { borderBottom: 'none' },
                                                }}
                                              >
                                                <TableCell sx={getSalesReturnItemBodyCellSx(isExpanded)}>
                                                  <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                    {item.item_name}
                                                  </Typography>
                                                  <Typography sx={SALES_RETURN_TABLE_SUBTEXT_SX}>
                                                    {item.item_code}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell sx={getSalesReturnItemBodyCellSx(isExpanded)}>
                                                  <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                    {item.batch_code}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell sx={getSalesReturnItemBodyCellSx(isExpanded)}>
                                                  <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                    {item.return_quantity ?? '-'}{item.return_uom_name ? ` / ${item.return_uom_name}` : ''}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell sx={getSalesReturnItemBodyCellSx(isExpanded)}>
                                                  <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                    {Number(item.mrp).toFixed(2)}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell sx={getSalesReturnItemBodyCellSx(isExpanded)}>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography sx={inlineLabelSx}>{t('Conc_Percentage_Short')}</Typography>
                                                    <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                      {item.concession_percentage ?? 0}%
                                                    </Typography>
                                                  </Box>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography sx={inlineLabelSx}>{t('Conc_Amount_Short')}</Typography>
                                                    <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                      {Number(item.concession_amount ?? 0).toFixed(2)}
                                                    </Typography>
                                                  </Box>
                                                </TableCell>
                                                <TableCell sx={getSalesReturnItemBodyCellSx(isExpanded)}>
                                                  <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                    {Number(item.total_amount).toFixed(2)}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell sx={getSalesReturnItemBodyCellSx(isExpanded)}>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography sx={inlineLabelSx}>{t('SGST_Percentage_Short')}</Typography>
                                                    <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                      {item.sgst_percentage ?? 0}%
                                                    </Typography>
                                                  </Box>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography sx={inlineLabelSx}>{t('SGST_Amount_Short')}</Typography>
                                                    <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                      {Number(item.sgst_amount ?? 0).toFixed(2)}
                                                    </Typography>
                                                  </Box>
                                                </TableCell>
                                                <TableCell sx={getSalesReturnItemBodyCellSx(isExpanded)}>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography sx={inlineLabelSx}>{t('CGST_Percentage_Short')}</Typography>
                                                    <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                      {item.cgst_percentage ?? 0}%
                                                    </Typography>
                                                  </Box>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography sx={inlineLabelSx}>{t('CGST_Amount_Short')}</Typography>
                                                    <Typography sx={SALES_RETURN_TABLE_BODY_TEXT_SX}>
                                                      {Number(item.cgst_amount ?? 0).toFixed(2)}
                                                    </Typography>
                                                  </Box>
                                                </TableCell>
                                                <TableCell sx={{ ...getSalesReturnItemBodyCellSx(isExpanded), textAlign: 'center' }}>
                                                  <Typography sx={{ ...SALES_RETURN_TABLE_BODY_TEXT_SX, textAlign: 'center' }}>
                                                    {Number(item.net_amount).toFixed(2)}
                                                  </Typography>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Typography sx={{ color: '#999', padding: 2 }}>
                                    {t('No_Items_Found')}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box borderTop={'1px solid #E2E3E5'}>
            <CustomPagination
              page={page}
              pageSize={rowsPerPage}
              totalCount={totalCount}
              pageSizeOptions={useServerPagination ? [10, 15, 25, 50] : undefined}
              onPageChange={(_, newPage) => setPage(newPage)}
              onPageSizeChange={useServerPagination ? handlePageSizeChange : undefined}
            />
          </Box>
          {actionRow && actionRow.status === 'pending' && (
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mt={2} px={1}>
              <Button
                variant="outlined"
                sx={secondaryButtonSx}
                onClick={() => actionRow && openRejectModal(actionRow)}
              >
                {t('Button_Reject')}
              </Button>
              <Button
                variant="contained"
                sx={{ ...primaryButtonSx }}
                onClick={() => actionRow && openApproveDialog(actionRow)}
              >
                {t('Button_Approve')}
              </Button>
            </Box>
          )}
        </Grid2>

        {/* Filter Modal */}
        <SalesReturnFilterModal
          open={isFilterModalOpen}
          onClose={handleFilterClose}
          onSave={handleFilterSave}
          initialValues={filterValues}
        />

        {/* Approve Dialog */}
        <IndentApproveCancelDialog
          open={approveDialogOpen}
          onClose={closeApproveDialog}
          onConfirm={confirmApprove}
          title={t('Confirm_Approve_Title')}
          message={t('Confirm_Approve_Message')}
          icon={
            <Box
              sx={{
                width: 140,
                height: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleRoundedIcon sx={{ width: 60, height: 60, color: '#4CAF50' }} />
            </Box>
          }
        />

        {/* Settle Payment Dialog */}
        <SalesReturnSettleDialog
          open={settlePaymentDialogOpen}
          onClose={closeSettlePaymentDialog}
          onAddToAdvance={openAddToAdvanceConfirmDialog}
          onRefundToAdvance={handleSettleRefundToPatient}
          title={t('Confirm_Settle_Title')}
          message={t('Confirm_Settle_Message')}
          icon={
            <Box
              sx={{
                width: 140,
                height: 140,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SettelIcon width={60} height={60} style={{ width: '60px', height: '60px' }} />
            </Box>
          }
        />

        {/* Add to Advance Confirmation Dialog */}
        <IndentApproveCancelDialog
          open={addToAdvanceConfirmDialogOpen}
          onClose={closeAddToAdvanceConfirmDialog}
          onConfirm={handleSettleAddToAdvance}
          title={t('Confirm_Advance_Title')}
          message={t('Confirm_Advance_Message')}
          icon={
            <Box
              sx={{
                width: 140,
                height: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DolorIcon width={60} height={60} style={{ width: '60px', height: '60px' }} />
            </Box>
          }
        />

        {/* Reject Modal with Formik */}
        <RejectSalesReturnModal
          open={rejectModalOpen}
          onClose={closeRejectModal}
          onSave={handleRejectSubmit}
        />

        <Popover
          open={Boolean(remarkAnchorEl)}
          anchorEl={remarkAnchorEl}
          onClose={closeRemarkPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '8px',
                padding: '12px 14px',
                maxWidth: 260,
              },
            },
          }}
        >
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600 }}>
            {remarkTitle} :&nbsp;
            <Typography
              component="span"
              sx={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#555', fontWeight: 400 }}
            >
              {remarkText}
            </Typography>
          </Typography>
        </Popover>
        {refundPaymentDetails && (
          <CollectPaymentModal
            open={refundModalOpen}
            onClose={() => setRefundModalOpen(false)}
            onSave={handleRefundSave}
            paymentDetails={refundPaymentDetails}
            existingPaymentHistory={[]}
            walletAdvances={[]}
            paymentModes={paymentModesForModal}
            hidePaymentModeToggle={true}
          />
        )}

        <SalesReturnReport
          open={printPreviewOpen}
          onClose={() => setPrintPreviewOpen(false)}
          data={selectedRowForPrint}
        />
      </Grid2>
    </>
  );
};

export default SalesReturnListBase;
